/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  onConnect,
  reaction,
  reatomAsync,
  withAssign,
} from '@reatom/framework'

import {
  AsyncStateN,
  localStorage,
  pipe,
  Record,
  Result,
} from '@blade/std-lib'
import { isLoggedInAtom } from '@root/entities/auth'
import { selectedNetworkAtom } from '@root/entities/network'
import { Network } from '@root/entities/network'
import { TradeSettings } from '@root/entities/trade-settings'
import { TradeSettingsByNetwork } from '@root/entities/trade-settings/model'
import { STORAGE_PREFIX } from '@root/shared'
import { reatomRequest } from '@root/shared/lib'
import { withEquivalence } from '@root/shared/lib/reatom/with-equivalence'

import { reatomDefaultTradeSettings } from './reatom-default-trade-settings'
import { initializeSolRpcSettingsAction } from './reatom-trade-settings-sol-rpc'
import {
  fetchUserTradeSettingsRequestAction,
  setActivePresetRequestAction,
} from './trade-settings-requests'

import type { TradeSettingsPreset } from '@root/entities/trade-settings/model'

const TRADE_SETTINGS_STORAGE_KEY = `${STORAGE_PREFIX}/v2/trade-settings`

const mapPresetsToActivePresetIndex = (
  presets: TradeSettingsByNetwork.PresetsByNetwork,
) => {
  return {
    [Network.Solana]: presets[Network.Solana].activePresetIndex,
    [Network.Ethereum]: presets[Network.Ethereum].activePresetIndex,
    [Network.Ton]: presets[Network.Ton].activePresetIndex,
    [Network.Base]: presets[Network.Base].activePresetIndex,
    [Network.Bsc]: presets[Network.Bsc].activePresetIndex,
  }
}

export const reatomTradeSettingsInternal = () => {
  const defaultSettingsModel = reatomDefaultTradeSettings()

  const initialState = (() => {
    const raw = localStorage.get(TRADE_SETTINGS_STORAGE_KEY)
    if (!raw) {
      return undefined
    }
    const settings = TradeSettingsByNetwork.fromJson(raw)
    if (!settings) {
      return undefined
    }
    return AsyncStateN.success(settings)
  })()

  const activePresetIndexByNetworkAtom = atom<
    Readonly<Record<Network.Network, number>>
  >(
    (() => {
      const presets = initialState?.value
      if (presets) {
        return mapPresetsToActivePresetIndex(presets)
      }
      return {
        [Network.Solana]: 0,
        [Network.Ethereum]: 0,
        [Network.Ton]: 0,
        [Network.Base]: 0,
        [Network.Bsc]: 0,
      }
    })(),
    'activePresetIndexByNetworkAtom',
  )

  const activePresetIndexAtom = atom((ctx) => {
    const byNetwork = ctx.spy(activePresetIndexByNetworkAtom)
    const network = ctx.spy(selectedNetworkAtom)
    return byNetwork[network]
  }, 'activePresetIndex')

  const tradeSettingsPresetsByNetwork = reatomRequest({
    execAction: fetchUserTradeSettingsRequestAction,
    initial: initialState,
    onResult: (ctx, result) => {
      if (Result.isSuccess(result)) {
        activePresetIndexByNetworkAtom(
          ctx,
          mapPresetsToActivePresetIndex(result.success),
        )
        const json = TradeSettingsByNetwork.toJson(result.success)
        if (json) {
          localStorage.set(TRADE_SETTINGS_STORAGE_KEY, json)
        }
      }
    },
  })

  const networkSettingsPresetsStateAtom = atom((ctx) => {
    const state = ctx.spy(tradeSettingsPresetsByNetwork)
    const network = ctx.spy(selectedNetworkAtom)
    return pipe(
      state,
      AsyncStateN.map((settings) => settings[network]),
    )
  }, 'networkSettingsStateAtom')

  const networkSettingsPresetStateAtom = atom((ctx) => {
    const activePresetIndex = ctx.spy(activePresetIndexAtom)
    const state = ctx.spy(networkSettingsPresetsStateAtom)
    return pipe(
      state,
      AsyncStateN.map((settings) => {
        return settings.presets[activePresetIndex]
      }),
    )
  }, 'networkSettingsStateAtom')

  const networkSettingsStateAtom = atom((ctx) => {
    const presetState = ctx.spy(networkSettingsPresetStateAtom)
    return pipe(
      presetState,
      AsyncStateN.map((preset) => preset?.settings),
    )
  }, 'networkSettingsStateAtom')

  const refreshAction = action(async (ctx) => {
    const result = await ctx.schedule(() =>
      fetchUserTradeSettingsRequestAction(ctx),
    )
    if (Result.isSuccess(result)) {
      tradeSettingsPresetsByNetwork.setState(
        ctx,
        AsyncStateN.success(result.success),
      )
    }
  })

  onConnect(tradeSettingsPresetsByNetwork, (ctx) => {
    if (AsyncStateN.isSuccess(ctx.get(defaultSettingsModel))) {
      return
    }
    defaultSettingsModel.execute(ctx)
  })

  const setTradeSettingsPresetsAction = action(
    (
      ctx,
      {
        presets,
        network,
      }: {
        presets: TradeSettingsByNetwork.Presets
        network: Network.Network
      },
    ) => {
      const settingsByNetwork = ctx.get(tradeSettingsPresetsByNetwork).value
      if (!settingsByNetwork) {
        return
      }

      const updatedSettings: TradeSettingsByNetwork.PresetsByNetwork = pipe(
        settingsByNetwork,
        Record.set(network, presets),
      )

      tradeSettingsPresetsByNetwork(ctx, AsyncStateN.success(updatedSettings))
      const json = TradeSettingsByNetwork.toJson(updatedSettings)
      if (json) {
        localStorage.set(TRADE_SETTINGS_STORAGE_KEY, json)
      }
    },
    'setTradeSettingsPresetsAction',
  )

  const setTradeSettingsAction = action(
    (
      ctx,
      {
        settings,
        network,
        presetIndex,
      }: {
        settings: TradeSettings.TradeSettings
        network: Network.Network
        presetIndex: number
      },
    ) => {
      const networkPresets = ctx.get(
        defaultSettingsModel.networkDefaultSettingsPresetsState,
      ).value

      const preset = networkPresets?.presets[presetIndex]
      if (!preset) {
        return
      }

      const updatedPreset: TradeSettingsPreset.Preset = {
        ...preset,
        settings: settings,
      }

      const updatedPresets = networkPresets.presets.map((preset, index) => {
        if (index === presetIndex) {
          return updatedPreset
        }
        return preset
      })

      const updatedSettings: TradeSettingsByNetwork.Presets = {
        ...networkPresets,
        presets: updatedPresets,
      }

      setTradeSettingsPresetsAction(ctx, {
        presets: updatedSettings,
        network,
      })
    },
    'updateTradeSettingsAction',
  )

  const setActivePresetAction = reatomAsync(async (ctx, preset: number) => {
    const network = ctx.get(selectedNetworkAtom)
    activePresetIndexByNetworkAtom(ctx, Record.set(network, preset))
    const result = await ctx.schedule(() =>
      setActivePresetRequestAction(ctx, {
        network,
        preset,
      }),
    )

    if (Result.isSuccess(result)) {
      setTradeSettingsPresetsAction(ctx, {
        presets: result.success,
        network,
      })
    }
  }, 'setActivePresetAction')

  const initializeAction = action((ctx) => {
    const state = ctx.get(tradeSettingsPresetsByNetwork)
    if (AsyncStateN.isSuccess(state)) {
      refreshAction(ctx)
      return
    }
    tradeSettingsPresetsByNetwork.execute(ctx)
  }, 'initializeAction')

  return tradeSettingsPresetsByNetwork.pipe(
    withAssign(() => ({
      activePresetIndex: activePresetIndexAtom,

      networkSettingsPresetsState: networkSettingsPresetsStateAtom,
      networkSettingsPresetState: networkSettingsPresetStateAtom,
      networkSettingsState: networkSettingsStateAtom,

      networkDefaultSettingsState:
        defaultSettingsModel.networkDefaultSettingsState,
      networkDefaultSettingsPresetsState:
        defaultSettingsModel.networkDefaultSettingsPresetsState,
      networkDefaultSettingsPresetState:
        defaultSettingsModel.networkDefaultSettingsPresetState,

      loadDefaultSettings: defaultSettingsModel.execute,
      initialize: initializeAction,
      setActivePreset: setActivePresetAction,
      setTradeSettings: setTradeSettingsAction,
      setTradeSettingsPresets: setTradeSettingsPresetsAction,
      refreshSettings: refreshAction,
    })),
  )
}

export type TradeSettingsModel = ReturnType<typeof reatomTradeSettingsInternal>

export const tradeSettingsModel = reatomTradeSettingsInternal()

export const tradeSettingsStateAtom = tradeSettingsModel.networkSettingsState
export const activePresetIndexAtom = tradeSettingsModel.activePresetIndex

export const tradeSettingsAtom = atom((ctx) => {
  const activePreset = ctx.spy(tradeSettingsModel.networkSettingsState).value

  if (!activePreset) {
    const network = ctx.spy(selectedNetworkAtom)
    return TradeSettings.nullSettings(network)
  }
  // TODO: Fallback to default settings
  return activePreset
}, 'tradeSettingsAtom').pipe(withEquivalence(TradeSettings.equivalence))

export const quickSwapAmountBySectionAtom = atom((ctx) => {
  const quickAmount = ctx.spy(tradeSettingsModel.networkSettingsPresetsState)
    .value?.quickAmounts
  return quickAmount
}, 'quickBuyAmountAtom')

export const initializeTradeSettingsAction = reaction((ctx) => {
  tradeSettingsModel.loadDefaultSettings(ctx)
  const isLoggedIn = ctx.spy(isLoggedInAtom)
  if (!isLoggedIn) {
    return
  }
  tradeSettingsModel.initialize(ctx)
  initializeSolRpcSettingsAction(ctx)
}, 'initializeTradeSettingsAction')
